import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, Camera, CheckCircle2, ClipboardEdit, FileText, Loader2, ShieldAlert, Zap,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import api, { API_URL, formatApiError } from "@/lib/api";
import LevelMeaning, { levelInfo } from "@/components/LevelMeaning";
import {
  ATTENDANCE_LABELS, ATTENDANCE_OPTIONS, DIRECTIONS, PERFORMANCE_AREAS,
  RESPONSE_LABELS, RESPONSE_OPTIONS, TRANSPORT_OPTIONS,
} from "@/config/trainingCatalog";

const OBS_FIELDS = [
  { key: "went_well", label: "What went well", required: true },
  { key: "further_training", label: "Areas requiring further training" },
  { key: "safety_concerns", label: "Safety concerns" },
  { key: "progress_since_previous", label: "Progress since previous session" },
  { key: "next_plan", label: "Plan for next session", required: true },
];

export default function SessionPage() {
  const { sessionId } = useParams();
  const [sess, setSess] = useState(null);
  const [denied, setDenied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [missing, setMissing] = useState([]);
  const [amendOpen, setAmendOpen] = useState(false);
  const [amendForm, setAmendForm] = useState({ reason: "", note: "" });
  const [purpose, setPurpose] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/sessions/${sessionId}`);
      setSess(data);
    } catch (err) {
      if (err?.response?.status === 404 || err?.response?.status === 403) setDenied(true);
      else toast.error(formatApiError(err));
    }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  const editable = sess && ["scheduled", "draft"].includes(sess.status);
  const patch = (fields) => setSess((s) => ({ ...s, ...fields }));
  const setObs = (k, v) => patch({ observations: { ...(sess.observations || {}), [k]: v } });
  const setIntervention = (k, v) => patch({ intervention: { ...(sess.intervention || {}), [k]: v } });
  const setPerf = (area, item, value) =>
    patch({ performance: { ...(sess.performance || {}), [area]: { ...((sess.performance || {})[area] || {}), items: { ...(((sess.performance || {})[area] || {}).items || {}), [item]: value } } } });
  const setPerfComment = (area, value) =>
    patch({ performance: { ...(sess.performance || {}), [area]: { ...((sess.performance || {})[area] || {}), comments: value } } });

  const save = async (submit = false) => {
    setSaving(true);
    setMissing([]);
    try {
      const body = {
        date: sess.date, direction: sess.direction, planned_start_time: sess.planned_start_time,
        actual_start_time: sess.actual_start_time, end_time: sess.end_time,
        start_location: sess.start_location, destination: sess.destination,
        transport_used: sess.transport_used, training_level: sess.training_level,
        attendance: sess.attendance, absence_reason: sess.absence_reason,
        performance: sess.performance, objectives_worked: sess.objectives_worked,
        intervention: sess.intervention, observations: sess.observations,
        status: "draft",
      };
      await api.patch(`/sessions/${sessionId}`, body);
      if (submit) {
        await api.post(`/sessions/${sessionId}/submit`);
        toast.success("Session submitted and locked");
      } else {
        toast.success("Draft saved — you can return later");
      }
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.missing) setMissing(detail.missing);
      toast.error(typeof detail === "object" ? detail.message : formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const copyPrevious = () => {
    const p = sess.previous_session;
    if (!p) return;
    patch({ transport_used: p.transport_used || [], training_level: p.training_level, performance: p.performance || {} });
    toast.success("Previous session responses copied — review before saving");
  };

  const uploadEvidence = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "training_evidence");
      fd.append("child_id", sess.child_id);
      fd.append("description", purpose || "Training evidence");
      const { data: doc } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/sessions/${sessionId}/evidence`, { document_id: doc.id, purpose: purpose || "Training evidence" });
      toast.success("Evidence attached (stored privately)");
      setPurpose("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitAmendment = async () => {
    setSaving(true);
    try {
      await api.post(`/sessions/${sessionId}/amend`, amendForm);
      toast.success("Amendment recorded — original submission preserved");
      setAmendOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (denied) {
    return (
      <div data-testid="session-access-denied" className="flex flex-col items-center justify-center py-20 text-center">
        <ShieldAlert className="h-10 w-10 text-red-600 mb-4" />
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Access denied</h1>
        <p className="text-sm text-slate-600 max-w-md">This session does not exist or you are not authorised. This attempt has been recorded.</p>
      </div>
    );
  }
  if (!sess) {
    return <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading session…</div>;
  }

  const isCompleted = sess.attendance === "completed";
  const levels = sess.independence_levels || [];

  return (
    <div className="space-y-4 pb-28" data-testid="session-page">
      <Link to={`/programmes/${sess.programme_id}`} data-testid="session-back-link" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
        <ArrowLeft className="h-4 w-4" /> Back to {sess.programme_number}
      </Link>

      <div className="rounded-lg border border-border bg-white p-4" data-testid="session-header">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="session-number">{sess.session_number}</h1>
          {sess.status === "draft" && <Badge className="bg-amber-100 text-amber-800 border-transparent text-sm px-3 py-1" data-testid="session-draft-badge">DRAFT</Badge>}
          {sess.status === "submitted" && <Badge className="bg-ukgreen-light text-ukgreen border-transparent" data-testid="session-submitted-badge">Submitted {(sess.submitted_at || "").slice(0, 10)}</Badge>}
          {sess.status === "scheduled" && <Badge className="bg-blue-50 text-blue-700 border-transparent">Scheduled</Badge>}
        </div>
        <p className="mt-1 text-sm text-slate-600">
          {sess.child?.child_name} · {sess.child?.case_id} · Trainer: <b>{sess.trainer_name}</b> (automatically recorded)
        </p>
      </div>

      {missing.length > 0 && (
        <div data-testid="session-completeness-errors" role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
          <p className="font-semibold">Session is incomplete — missing:</p>
          <ul className="list-disc pl-5">{missing.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}

      {editable && sess.previous_session && (
        <Button variant="outline" data-testid="session-copy-previous" className="h-11 border-blue-300 text-blue-700" onClick={copyPrevious}>
          <Zap className="h-4 w-4 mr-1" /> Quick start: copy responses from {sess.previous_session.date}
        </Button>
      )}

      <fieldset disabled={!editable} className="space-y-4">
        {/* Attendance first — one tap for the common case */}
        <Card>
          <CardHeader><CardTitle className="text-base">Attendance / outcome *</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2" data-testid="attendance-options">
              {ATTENDANCE_OPTIONS.map((o) => (
                <button key={o.value} type="button" data-testid={`attendance-${o.value}`}
                  onClick={() => patch({ attendance: o.value })}
                  className={`h-12 rounded-md border px-3 text-sm font-medium text-left transition-colors duration-150 ${sess.attendance === o.value ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-border hover:bg-muted"}`}>
                  {o.label}
                </button>
              ))}
            </div>
            {sess.attendance && !isCompleted && (
              <div className="mt-3 space-y-1.5">
                <Label>Reason / comment *</Label>
                <Textarea data-testid="absence-reason" rows={2} value={sess.absence_reason || ""} onChange={(e) => patch({ absence_reason: e.target.value })} />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Session details */}
        <Card>
          <CardHeader><CardTitle className="text-base">Session details</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label>Journey direction *</Label>
              <div className="grid grid-cols-3 gap-2">
                {DIRECTIONS.map((d) => (
                  <button key={d.value} type="button" data-testid={`direction-${d.value}`} onClick={() => patch({ direction: d.value })}
                    className={`h-12 rounded-md border px-2 text-xs font-medium transition-colors duration-150 ${sess.direction === d.value ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-border hover:bg-muted"}`}>
                    {d.short}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Route plan used</Label>
              <div className="grid grid-cols-2 gap-2">
                {[["primary", "Primary route plan"], ["alternative", "Alternative / emergency route plan"]].map(([val, lab]) => (
                  <button key={val} type="button" data-testid={`route-used-${val}`}
                    onClick={() => patch({ route_used: val, ...(val === "primary" ? { alternative_reason: null } : {}) })}
                    className={`h-12 rounded-md border px-2 text-xs font-medium transition-colors duration-150 ${sess.route_used === val ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-border hover:bg-muted"}`}>
                    {lab}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500">The alternative route plan may only be used in an emergency or when the primary route is blocked, and only once it has been risk assessed and approved.</p>
            </div>
            {sess.route_used === "alternative" && (
              <div className="space-y-1.5">
                <Label>Reason the alternative route was used *</Label>
                <Textarea data-testid="session-alternative-reason" rows={2} value={sess.alternative_reason || ""}
                  onChange={(e) => patch({ alternative_reason: e.target.value })}
                  placeholder="e.g. Primary bus cancelled, road closed, station closed…" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Date *</Label><Input data-testid="session-date" type="date" value={sess.date || ""} onChange={(e) => patch({ date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Planned start</Label><Input data-testid="session-planned-start" type="time" value={sess.planned_start_time || ""} onChange={(e) => patch({ planned_start_time: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Actual start{isCompleted ? " *" : ""}</Label><Input data-testid="session-actual-start" type="time" value={sess.actual_start_time || ""} onChange={(e) => patch({ actual_start_time: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>End time{isCompleted ? " *" : ""}</Label><Input data-testid="session-end" type="time" value={sess.end_time || ""} onChange={(e) => patch({ end_time: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Starting location</Label><Input data-testid="session-start-location" value={sess.start_location || ""} onChange={(e) => patch({ start_location: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Destination</Label><Input data-testid="session-destination" value={sess.destination || ""} onChange={(e) => patch({ destination: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Transport used</Label>
              <div className="flex flex-wrap gap-2">
                {TRANSPORT_OPTIONS.map((t) => {
                  const on = (sess.transport_used || []).includes(t.value);
                  return (
                    <button key={t.value} type="button" data-testid={`transport-${t.value}`}
                      onClick={() => patch({ transport_used: on ? (sess.transport_used || []).filter((x) => x !== t.value) : [...(sess.transport_used || []), t.value] })}
                      className={`h-11 px-4 rounded-md border text-sm font-medium transition-colors duration-150 ${on ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-border hover:bg-muted"}`}>
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Training level{isCompleted ? " *" : ""}</Label>
              <Select value={sess.training_level ? String(sess.training_level) : ""} onValueChange={(v) => patch({ training_level: parseInt(v, 10) })}>
                <SelectTrigger data-testid="session-level" className="h-12"><SelectValue placeholder="Select level" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {levels.map((l) => (
                    <SelectItem key={l.level} value={String(l.level)} data-testid={`session-level-option-${l.level}`}>
                      <div className="text-left">
                        <div>L{l.level} — {l.name}</div>
                        <div className="text-xs text-slate-500 font-normal">{l.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {sess.training_level && <LevelMeaning levels={levels} level={sess.training_level} testid="session-level-meaning" />}
            </div>
            {(sess.objectives || []).length > 0 && (
              <div className="space-y-1.5">
                <Label>Objectives worked on</Label>
                <div className="flex flex-wrap gap-2">
                  {sess.objectives.map((o) => {
                    const on = (sess.objectives_worked || []).includes(o.id);
                    return (
                      <button key={o.id} type="button" data-testid={`objective-chip-${o.id}`}
                        onClick={() => patch({ objectives_worked: on ? (sess.objectives_worked || []).filter((x) => x !== o.id) : [...(sess.objectives_worked || []), o.id] })}
                        className={`min-h-11 px-3 rounded-md border text-xs font-medium transition-colors duration-150 ${on ? "bg-ukgreen text-white border-ukgreen" : "bg-white text-slate-700 border-border hover:bg-muted"}`}>
                        {o.objective.length > 48 ? o.objective.slice(0, 48) + "…" : o.objective}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Performance — only for completed sessions */}
        {isCompleted && (
          <Card data-testid="performance-section">
            <CardHeader><CardTitle className="text-base">Daily performance record</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              {PERFORMANCE_AREAS.map((area) => (
                <div key={area.key} className="rounded-md border border-border p-3" data-testid={`perf-area-${area.key}`}>
                  <p className="font-semibold text-sm text-slate-900 mb-2">{area.label}</p>
                  <div className="space-y-2">
                    {area.items.map((item) => (
                      <div key={item.key} className="flex items-center gap-2">
                        <span className="flex-1 text-xs text-slate-700">{item.label}</span>
                        <Select value={sess.performance?.[area.key]?.items?.[item.key] || ""}
                          onValueChange={(v) => setPerf(area.key, item.key, v)}>
                          <SelectTrigger data-testid={`perf-${area.key}-${item.key}`} className="h-11 w-44"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent className="bg-white">
                            {RESPONSE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                  <Input className="mt-2" placeholder={`${area.label} comments (optional)`} data-testid={`perf-comment-${area.key}`}
                    value={sess.performance?.[area.key]?.comments || ""} onChange={(e) => setPerfComment(area.key, e.target.value)} />
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Intervention */}
        <Card data-testid="intervention-section">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> Trainer intervention</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {[false, true].map((v) => (
                <button key={String(v)} type="button" data-testid={`intervention-occurred-${v}`} onClick={() => setIntervention("occurred", v)}
                  className={`h-12 rounded-md border text-sm font-medium transition-colors duration-150 ${(sess.intervention?.occurred ?? false) === v ? (v ? "bg-red-600 text-white border-red-600" : "bg-ukgreen text-white border-ukgreen") : "bg-white text-slate-700 border-border hover:bg-muted"}`}>
                  {v ? "Intervention occurred" : "No intervention"}
                </button>
              ))}
            </div>
            {sess.intervention?.occurred && (
              <div className="space-y-3 rounded-md border border-red-200 bg-red-50/40 p-3">
                <p className="text-xs text-slate-600">An intervention is not automatically a safeguarding incident. Escalate to the Incident module (later phase) only when appropriate.</p>
                {[
                  ["what_happened", "What happened *"], ["location", "Location / journey stage"],
                  ["reason", "Reason for intervention *"], ["action_taken", "Action taken *"],
                  ["immediate_outcome", "Immediate outcome"], ["further_training_required", "Further training required"],
                ].map(([k, label]) => (
                  <div key={k} className="space-y-1.5">
                    <Label>{label}</Label>
                    <Textarea data-testid={`intervention-${k}`} rows={2} value={sess.intervention?.[k] || ""} onChange={(e) => setIntervention(k, e.target.value)} />
                  </div>
                ))}
                <div className="grid grid-cols-1 gap-2">
                  {[["risk_review_needed", "Does the risk assessment need review?"], ["manager_notification", "Does the manager need notification?"], ["incident_required", "Does this require an Incident Report?"]].map(([k, label]) => (
                    <div key={k} className="flex items-center justify-between rounded-md border border-border bg-white px-3 py-2">
                      <span className="text-xs font-medium text-slate-700">{label}</span>
                      <div className="flex gap-1">
                        {[true, false].map((v) => (
                          <button key={String(v)} type="button" data-testid={`intervention-${k}-${v}`} onClick={() => setIntervention(k, v)}
                            className={`h-9 px-4 rounded-md text-xs font-semibold ${sess.intervention?.[k] === v ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>
                            {v ? "Yes" : "No"}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Observations */}
        {isCompleted && (
          <Card data-testid="observations-section">
            <CardHeader><CardTitle className="text-base">Session observations *</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-slate-500">Keep notes factual and objective. Text fields support device voice-to-text.</p>
              {OBS_FIELDS.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label>{f.label}{f.required ? " *" : ""}</Label>
                  <Textarea data-testid={`obs-${f.key}`} rows={2} value={sess.observations?.[f.key] || ""} onChange={(e) => setObs(f.key, e.target.value)} />
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </fieldset>

      {/* Evidence */}
      <Card data-testid="evidence-section">
        <CardHeader><CardTitle className="text-base">Photo / evidence (optional)</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(sess.evidence || []).map((e, i) => (
            <p key={i} className="text-xs text-slate-600" data-testid={`evidence-${i}`}>
              {e.purpose} — uploaded by {e.uploaded_by_name} (consent: {e.consent_status})
            </p>
          ))}
          <div className="flex flex-wrap gap-2 items-center">
            <Input className="h-11 flex-1 min-w-[180px]" placeholder="Purpose of evidence" data-testid="evidence-purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
            <Button variant="outline" className="h-11" data-testid="evidence-upload-button" onClick={() => fileRef.current?.click()}>
              <Camera className="h-4 w-4 mr-1" /> Attach evidence
            </Button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="evidence-input"
              onChange={(e) => { uploadEvidence(e.target.files?.[0]); e.target.value = ""; }} />
          </div>
          <p className="text-xs text-slate-500">Evidence is stored privately and only accessible to authorised staff. Trainers cannot delete official evidence after submission.</p>
        </CardContent>
      </Card>

      {/* Submitted: amendments + PDF */}
      {sess.status === "submitted" && (
        <Card data-testid="amendments-section">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Amendment history</CardTitle>
            {sess.can_amend && (
              <Button variant="outline" className="h-11" data-testid="amend-open-button" onClick={() => setAmendOpen(true)}>
                <ClipboardEdit className="h-4 w-4 mr-1" /> Add amendment
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {!sess.amendments?.length ? (
              <p className="text-sm text-slate-600">No amendments. The original submitted record is preserved.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {sess.amendments.map((a, i) => (
                  <li key={i} className="rounded-md border border-border p-3" data-testid={`amendment-${i}`}>
                    <p className="font-medium text-slate-800">{a.reason}</p>
                    {a.note && <p className="text-slate-600">{a.note}</p>}
                    <p className="text-xs text-slate-500">{(a.at || "").slice(0, 16).replace("T", " ")} by {a.author_name}</p>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sticky action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-white/95 backdrop-blur px-4 py-3" data-testid="session-action-bar">
        <div className="mx-auto flex max-w-3xl gap-2">
          {editable ? (
            <>
              <Button variant="outline" className="h-12 flex-1 font-semibold" data-testid="session-save-draft" disabled={saving} onClick={() => save(false)}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save draft"}
              </Button>
              <Button className="h-12 flex-1 bg-ukgreen hover:bg-ukgreen/90 text-white font-semibold" data-testid="session-submit" disabled={saving} onClick={() => save(true)}>
                <CheckCircle2 className="h-4 w-4 mr-1" /> Submit session
              </Button>
            </>
          ) : (
            <a href={`${API_URL}/sessions/${sess.id}/pdf`} target="_blank" rel="noreferrer" data-testid="session-pdf-button"
              className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-md bg-primary text-sm font-semibold text-white hover:bg-brand-hover">
              <FileText className="h-4 w-4" /> Session record PDF
            </a>
          )}
        </div>
      </div>

      <Dialog open={amendOpen} onOpenChange={setAmendOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Add amendment</DialogTitle><DialogDescription>The original submitted record is never overwritten — both are shown to authorised users.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Reason *</Label><Input data-testid="amend-reason" value={amendForm.reason} onChange={(e) => setAmendForm((f) => ({ ...f, reason: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Amendment detail</Label><Textarea data-testid="amend-note" rows={3} value={amendForm.note} onChange={(e) => setAmendForm((f) => ({ ...f, note: e.target.value }))} /></div>
            <Button data-testid="amend-submit" className="h-11 w-full bg-primary text-white" disabled={saving || !amendForm.reason.trim()} onClick={submitAmendment}>Record amendment</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
